<template>
  <div class="c-modal c-modal--message-improve" :class="{ 'is-active': isActive }">
    <div class="c-modal__bg" @click="$emit('close')"></div>
    <div class="c-modal__content">
      <div class="c-modal__close" @click="$emit('close')">
        <div class="c-icon c-icon--white u-wh14">
          <svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
            <use xlink:href="/assets/img/sprite.svg#close" />
          </svg>
        </div>
      </div>
      <div class="c-modal__inner">
        <div class="c-modal__title">通知リスクチェック</div>

        <!-- ローディング -->
        <div v-if="isLoading" class="message-improve__loading">
          <div class="message-improve__spinner"></div>
          <p>分析中...</p>
        </div>

        <!-- 結果表示 -->
        <template v-if="!isLoading && result">
          <!-- リスクスコア -->
          <div class="message-improve__score">
            <div class="message-improve__score-label">リスクスコア</div>
            <div class="message-improve__score-value" :style="{ color: scoreColor }">
              {{ result.risk_score }}
              <span class="message-improve__score-level">/ 100 ({{ result.risk_level }})</span>
            </div>
            <div class="message-improve__score-bar">
              <div
                class="message-improve__score-bar-fill"
                :style="{ width: Math.max(0, Math.min(100, result.risk_score)) + '%', backgroundColor: scoreColor }"
              ></div>
            </div>
          </div>

          <!-- 改善案（比較表示） -->
          <div v-if="result.suggestions" class="message-improve__section">
            <div class="message-improve__section-title">改善案</div>

            <!-- タイトル比較 -->
            <div v-if="result.suggestions.title" class="message-improve__compare">
              <div class="message-improve__compare-label">タイトル</div>
              <div class="message-improve__compare-row">
                <div class="message-improve__compare-cell message-improve__compare-cell--before">
                  <div class="message-improve__compare-tag message-improve__compare-tag--before">改善前</div>
                  <div class="message-improve__compare-text">{{ originalTitle || '（未入力）' }}</div>
                </div>
                <div class="message-improve__compare-arrow">&#8594;</div>
                <div class="message-improve__compare-cell message-improve__compare-cell--after">
                  <div class="message-improve__compare-tag message-improve__compare-tag--after">改善後</div>
                  <div class="message-improve__compare-text">{{ result.suggestions.title }}</div>
                </div>
              </div>
            </div>

            <!-- 本文比較 -->
            <div v-if="result.suggestions.text" class="message-improve__compare">
              <div class="message-improve__compare-label">本文</div>
              <div class="message-improve__compare-row">
                <div class="message-improve__compare-cell message-improve__compare-cell--before">
                  <div class="message-improve__compare-tag message-improve__compare-tag--before">改善前</div>
                  <div class="message-improve__compare-text">{{ originalText || '（未入力）' }}</div>
                </div>
                <div class="message-improve__compare-arrow">&#8594;</div>
                <div class="message-improve__compare-cell message-improve__compare-cell--after">
                  <div class="message-improve__compare-tag message-improve__compare-tag--after">改善後</div>
                  <div class="message-improve__compare-text">{{ result.suggestions.text }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- 問題点 -->
          <div v-if="result.issues && result.issues.length > 0" class="message-improve__section">
            <div class="message-improve__section-title">検出された問題点</div>
            <div class="message-improve__issues">
              <div v-for="(issue, index) in result.issues" :key="index" class="message-improve__issue-item">
                <template v-if="typeof issue === 'object' && issue.factor">
                  <div class="message-improve__issue-factor">{{ issue.factor }}</div>
                  <div class="message-improve__issue-detail">{{ issue.detail }}</div>
                </template>
                <template v-else>
                  <div class="message-improve__issue-detail">{{ issue }}</div>
                </template>
              </div>
            </div>
          </div>

          <!-- 改善ポイント詳細 -->
          <div v-if="result.suggestions && result.suggestions.reasoning" class="message-improve__section">
            <div class="message-improve__reasoning-section">
              <div class="message-improve__reasoning-toggle" @click="reasoningOpen = !reasoningOpen">
                <span class="message-improve__reasoning-arrow" :class="{ 'is-open': reasoningOpen }">&#9654;</span>
                改善ポイントの詳細
              </div>
              <div v-if="reasoningOpen" class="message-improve__reasoning-body">
                <ol v-if="reasoningItems.length > 1" class="message-improve__reasoning-list">
                  <li v-for="(item, index) in reasoningItems" :key="index">{{ item }}</li>
                </ol>
                <p v-else class="message-improve__reasoning-text">{{ result.suggestions.reasoning }}</p>
              </div>
            </div>
          </div>
        </template>

        <!-- 注釈 -->
        <p
          v-if="!isLoading && result"
          class="message-improve__disclaimer"
        >
          ※ 本診断はAIによる参考情報であり、実際の通知判定を保証するものではありません。<br />より安全な配信に向けた改善のヒントとしてご活用ください。
        </p>

        <!-- ボタン -->
        <div v-if="!isLoading" class="c-modal__btns">
          <button
            class="c-btn c-btn--modal c-btn--gray u-w140 u-h34 u-fs14"
            type="button"
            @click="$emit('close')"
          >
            <p class="c-btn__text">閉じる</p>
          </button>
          <button
            v-if="hasSuggestions"
            class="c-btn c-btn--modal c-btn--blue u-w140 u-h34 u-fs14"
            type="button"
            @click="applySuggestions"
          >
            <p class="c-btn__text">適用する</p>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    isActive: { type: Boolean, required: true },
    result: { type: Object, default: null },
    isLoading: { type: Boolean, default: false },
    originalTitle: { type: String, default: '' },
    originalText: { type: String, default: '' },
  },
  emits: ["close","apply"],
  data() {
    return {
      reasoningOpen: false,
    };
  },
  computed: {
    reasoningItems() {
      if (!this.result || !this.result.suggestions || !this.result.suggestions.reasoning) return [];
      const text = this.result.suggestions.reasoning;
      // "(1)...(2)..." or "（1）...（2）..." 形式を分割
      const parts = text.split(/[(（]\d+[)）]/).filter(s => s.trim());
      return parts.map(s => s.replace(/^[：:\s]+/, '').trim()).filter(Boolean);
    },
    scoreColor() {
      if (!this.result) return '#999';
      const score = this.result.risk_score;
      if (score <= 20) return '#63cb36';  // $colorSecondlyGreen1
      if (score <= 40) return '#209bb9';  // $colorPrimary1
      if (score <= 60) return '#3cb6d2';  // $colorPrimary2
      if (score <= 80) return '#f2657f';  // $colorSecondlyPink1
      return '#ea2d2d';                   // $colorFunctionalError1
    },
    hasSuggestions() {
      return this.result
        && this.result.suggestions
        && (this.result.suggestions.title || this.result.suggestions.text);
    },
  },
  watch: {
    isActive(val) {
      if (val) this.reasoningOpen = false;
    },
  },
  methods: {
    applySuggestions() {
      this.$emit('apply', {
        title: this.result.suggestions.title || '',
        text: this.result.suggestions.text || '',
      });
    },
  },
};
</script>
